import React from "react";
import constants from "../constants";
import { useAuth } from "./AuthContext";

export interface ICaseData {
  receiptNumber: string;
  submittedDate?: string;
  formType: string;
  tracking: boolean;
  createdAt: string;
  status: string; // e.g., 'pending', 'approved', 'rejected'
}

export interface ICaseTrackingContext {
  cases: ICaseData[];
  caseDetails: any;
  setCaseDetails: React.Dispatch<React.SetStateAction<any>>;
  createNewCase: (receiptNumber: string) => void;
  getAllCases: () => void;
  getParticularCase: (receiptNumber: string) => void;
  updateCaseTracking: (receiptNumber: string, tracking: boolean) => void;
  refreshCase: (receiptNumber: string) => void;
  deleteCaseTracking: (receiptNumber: string) => void;
}

const defaultValues: ICaseTrackingContext = {
  cases: [],
  caseDetails: null,
  setCaseDetails: () => { },
  createNewCase: () => { },
  getAllCases: () => { },
  getParticularCase: () => { },
  updateCaseTracking: () => { },
  refreshCase: () => { },
  deleteCaseTracking: () => { },
};

const context = React.createContext(defaultValues);

export function useCaseTrackingContext() {
  return React.useContext(context);
}

export const CaseTrackingProvider: React.FC = (props) => {
  const [cases, setCases] = React.useState<ICaseData[]>(defaultValues.cases);
  const [caseDetails, setCaseDetails] = React.useState<any>(null);
  const { currentUser } = useAuth();

  const getAllCases = React.useCallback(async () => {
    try {
      if (!currentUser || !currentUser.uid) {
        console.error("User not authenticated");
        return;
      }
      const token = await currentUser.getIdToken();
      const res = await fetch(
        `${constants.middleHost}/case/get/${currentUser.uid}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) {
        throw new Error(`Error fetching cases: ${res.statusText}`);
      }
      const data = await res.json();
      console.log("Fetched cases:", data);

      if (data.status === 200) {
        setCases(data.data);
      } else {
        console.error("Failed to fetch cases:", data.error);
      }
    } catch (error) {
      console.error("Error fetching cases:", error);
    }
  }, [currentUser]);

  const createNewCase = React.useCallback(
    async (receiptNumber) => {
      try {
        if (!currentUser || !currentUser.uid) {
          console.error("User not authenticated");
          return;
        }
        const token = await currentUser.getIdToken();
        const res = await fetch(`${constants.middleHost}/case/new`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            uid: currentUser.uid,
            receiptNumber: receiptNumber,
          }),
        });
        if (!res.ok) {
          throw new Error(`Error creating case: ${res.statusText}`);
        }
        const data = await res.json();
        setCases((prevCases) => [...prevCases, data.data]);
      } catch (error) {
        console.error("Error creating case:", error);
      }
    },
    [currentUser]
  );

  const getParticularCase = React.useCallback(
    async (receiptNumber) => {
      try {
        setCaseDetails(null);
        if (!currentUser || !currentUser.uid) {
          console.error("User not authenticated");
          return;
        }
        const token = await currentUser.getIdToken();
        const res = await fetch(
          `${constants.middleHost}/case/get/${currentUser.uid}/receiptNumber/${receiptNumber}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!res.ok) {
          throw new Error(`Error fetching case: ${res.statusText}`);
        }
        const data = await res.json();
        const finalData = {
          ...JSON.parse(data.data.payload),
          tracking: data.data.tracking,
          refreshAt: data.data.refreshAt,
        }
        setCaseDetails(finalData);
        if (data.status === 200) {
          return finalData;
        } else {
          console.error("Failed to fetch case:", data.error);
          return null;
        }
      } catch (error) {
        console.error("Error fetching case:", error);
        return null;
      }
    },
    [currentUser]
  );

  const updateCaseTracking = React.useCallback(
    async (receiptNumber, tracking) => {
      try {
        if (!currentUser || !currentUser.uid) {
          console.error("User not authenticated");
          return;
        }
        const token = await currentUser.getIdToken();
        const res = await fetch(`${constants.middleHost}/case/update-tracking`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            uid: currentUser.uid,
            receiptNumber: receiptNumber,
            tracking: tracking,
          }),
        });
        if (!res.ok) {
          throw new Error(`Error updating case tracking: ${res.statusText}`);
        }
        const data = await res.json();
        if (data.status === 200) {
          setCases((prevCases) =>
            prevCases.map((caseItem) =>
              caseItem.receiptNumber === receiptNumber
                ? { ...caseItem, tracking: tracking }
                : caseItem
            )
          );
        } else {
          console.error("Failed to update case tracking:", data.error);
        }
      } catch (error) {
        console.error("Error updating case tracking:", error);
      }
    },
    [currentUser]
  );

  const deleteCaseTracking = React.useCallback(
    async (receiptNumber) => {
      try {
        if (!currentUser || !currentUser.uid) {
          console.error("User not authenticated");
          return;
        }
        const token = await currentUser.getIdToken();
        const res = await fetch(`${constants.middleHost}/case/delete`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            uid: currentUser.uid,
            receiptNumber: receiptNumber,
          }),
        });
        if (!res.ok) {
          throw new Error(`Error deleting case: ${res.statusText}`);
        } else {
          setCases((prevCases) =>
            prevCases.filter(
              (caseItem) => caseItem.receiptNumber !== receiptNumber
            )
          );
        }
      } catch (error) {
        console.error("Error deleting case:", error);
      }
    },
    [currentUser]
  );

  const refreshCase = React.useCallback(
    async (receiptNumber: string) => {
      try {
        const token = await currentUser?.getIdToken();
        const res = await fetch(`${constants.middleHost}/case/refresh`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            uid: currentUser?.uid,
            receiptNumber: receiptNumber,
          }),
        });
        if (!res.ok) {
          throw new Error(`Error refreshing case: ${res.statusText}`);
        }
        const data = await res.json();
        const finalData = {
          ...JSON.parse(data.data.payload),
          tracking: data.data.tracking,
          refreshAt: data.data.refreshAt,
        }
        setCaseDetails(finalData);
      } catch (error) {
        console.error("Error refreshing case:", error);
      }
    },
    [currentUser]
  );

  return (
    <context.Provider
      value={{
        cases,
        caseDetails,
        setCaseDetails,
        createNewCase,
        getAllCases,
        getParticularCase,
        updateCaseTracking,
        refreshCase,
        deleteCaseTracking,
      }}
    >
      {props.children}
    </context.Provider>
  );
};
